import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { cn } from "@/utils/helpers";
import {
  isOptimisticInvite,
  useBrandGuests,
  useCancelBrandGuestInvite,
  useInviteBrandGuest,
  useRemoveBrandGuest,
  type BrandGuest,
  type BrandGuestInvite,
} from "@/hooks/useBrandGuests";

/**
 * Guests (Figma 08, 7 Managing): the brand's people who see the calendar and
 * answer what the team sends, each with their own login. Any guest can
 * approve; the review records who did (no named approver). Owners invite,
 * remove (confirmed in the row itself) and cancel invites; Contributors see
 * the list.
 */

const LABEL = "font-mono text-[11px] uppercase tracking-[0.16em] text-co-muted";
const SECONDARY =
  "inline-flex h-9 shrink-0 items-center rounded-pill bg-co-tonal px-4 text-[13px] font-medium text-co-text shadow-[inset_0_0_0_1px_var(--co-line2)] outline-none transition-colors hover:bg-co-hover focus-visible:outline-2 focus-visible:outline-co-accent disabled:opacity-50";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

function dayLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).replace(",", "");
}

function GuestSkeletons() {
  return (
    <div className="flex flex-col gap-2.5" aria-hidden="true">
      {[0, 1].map((i) => (
        <div key={i} className="flex h-[72px] items-center gap-3.5 rounded-card border border-co-line2 px-4">
          <span className="size-9 rounded-[8px] bg-co-tonal motion-safe:animate-pulse" />
          <span className="flex flex-1 flex-col gap-2">
            <span className="h-3.5 w-32 rounded-[6px] bg-co-tonal motion-safe:animate-pulse" />
            <span className="h-3 w-44 rounded-[6px] bg-co-tonal motion-safe:animate-pulse" />
          </span>
        </div>
      ))}
    </div>
  );
}

function GuestRow({ guest, brandName, canManage, onRemove }: { guest: BrandGuest; brandName: string; canManage: boolean; onRemove: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const name = guest.name?.trim() || guest.email;
  const first = name.split(/\s+/)[0];
  return (
    <div className="flex flex-wrap items-center gap-3.5 rounded-card border border-co-line2 px-4 py-3.5">
      <span aria-hidden="true" className="grid size-9 shrink-0 place-items-center rounded-[8px] bg-co-tonal text-[12px] font-bold text-co-text2">
        {initials(name)}
      </span>
      {confirming ? (
        <>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-[15px] font-medium text-co-text">Remove {first}?</span>
            <span className="text-[13px] leading-[1.45] text-co-text2">
              {first} stops seeing {brandName} at once. Nothing they wrote is deleted.
            </span>
          </span>
          <button type="button" onClick={() => setConfirming(false)} className={SECONDARY}>
            Keep
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex h-9 shrink-0 items-center rounded-pill bg-co-danger px-4 text-[13px] font-medium text-white outline-none hover:bg-co-danger-hover focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            Remove access
          </button>
        </>
      ) : (
        <>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-[15px] font-medium text-co-text">{name}</span>
            <span className="truncate text-[13px] text-co-text2">{guest.name ? `${guest.email} · ` : ""}Sees what you send</span>
          </span>
          <span className="text-[13.5px] text-co-text2">Reads, comments and approves</span>
          {canManage ? (
            <button type="button" onClick={() => setConfirming(true)} className={SECONDARY}>
              Remove
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

function InviteRow({ invite, canManage, onCancel }: { invite: BrandGuestInvite; canManage: boolean; onCancel: () => void }) {
  const pending = isOptimisticInvite(invite);
  return (
    <div className="flex flex-wrap items-center gap-3.5 rounded-card border border-dashed border-co-line2 py-3.5 pl-[66px] pr-4">
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[15px] font-medium text-co-text">{invite.email}</span>
        <span className="text-[13px] text-co-text2">{pending ? "Sending the invite…" : `Invite sent. It ends ${dayLabel(invite.expiresAt)}.`}</span>
      </span>
      <span className="text-[13.5px] text-co-text2">Invited</span>
      {canManage && !pending ? (
        <button type="button" onClick={onCancel} className={SECONDARY}>
          Cancel invite
        </button>
      ) : null}
    </div>
  );
}

export default function BrandGuestsModal({
  brandId,
  brandName = "this brand",
  canManage,
  onClose,
}: {
  brandId: string;
  brandName?: string;
  /** Owner: invite and remove guests. Contributor: see the list. */
  canManage: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useBrandGuests(brandId);
  const invite = useInviteBrandGuest(brandId);
  const removeGuest = useRemoveBrandGuest(brandId);
  const cancelInvite = useCancelBrandGuestInvite(brandId);

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const domain = brandName.toLowerCase().replace(/[^a-z0-9]+/g, "") || "client";

  const handleInvite = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setError("Type the email of the person to invite.");
      return;
    }
    invite.mutate(trimmed);
    setEmail("");
    setError(null);
  };

  const guests = data?.guests ?? [];
  const invites = data?.invites ?? [];

  return (
    <KitModal
      isOpen
      onOpenChange={(open) => !open && onClose()}
      title="Guests"
      description={`${brandName}’s people who see the calendar and answer what you send. Each signs in with their own login. They change nothing else and never see costs.`}
      modalClassName="max-w-[600px]"
    >
      <div className="flex flex-col gap-5">
        {canManage ? (
          <form onSubmit={handleInvite} className="flex flex-col gap-2">
            <label htmlFor="guest-invite-email" className={LABEL}>
              Invite by email
            </label>
            <div className="flex items-center gap-2">
              <input
                id="guest-invite-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setError(null);
                }}
                placeholder={`name@${domain}.com`}
                aria-invalid={!!error || undefined}
                className={cn(
                  "h-11 min-w-0 flex-1 rounded-card bg-co-tonal px-4 text-[14px] text-co-text outline-none placeholder:text-co-muted focus:shadow-[inset_0_0_0_1px_var(--co-accent)]",
                  error && "shadow-[inset_0_0_0_1px_var(--co-danger)]",
                )}
              />
              <KitButton type="submit" className="h-11">
                Invite
              </KitButton>
            </div>
            <p className={cn("text-[13px] leading-[1.45]", error ? "text-co-alert-ink" : "text-co-text2")}>
              {error ?? `Someone who already has a login sees ${brandName} at once. Anyone else gets an email to make one.`}
            </p>
          </form>
        ) : null}

        <div className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between">
            <p className={LABEL}>Can see {brandName}</p>
            {guests.length || invites.length ? (
              <p className={LABEL}>
                {guests.length}
                {invites.length ? ` · ${invites.length} invited` : ""}
              </p>
            ) : null}
          </div>
          {isLoading ? (
            <GuestSkeletons />
          ) : isError ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-co-line2 px-4 py-3">
              <p className="text-[13px] text-co-muted">The guests could not load.</p>
              <button type="button" onClick={() => void refetch()} className="text-[13px] font-medium text-co-accent-ink outline-none hover:underline">
                Try again
              </button>
            </div>
          ) : guests.length === 0 && invites.length === 0 ? (
            <div className="flex flex-col gap-1.5 rounded-card border border-dashed border-co-line2 px-5 py-6">
              <p className="text-[15px] font-medium text-co-text">Nobody outside the team yet</p>
              <p className="text-[13px] leading-[1.45] text-co-text2">
                {canManage
                  ? `Invite ${brandName}’s people above. They see the calendar, answer what you send and comment. They take no seat and never see costs.`
                  : `An owner can invite ${brandName}’s people. They see the calendar, answer what you send and comment.`}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {guests.map((guest) => (
                <GuestRow key={guest.userId} guest={guest} brandName={brandName} canManage={canManage} onRemove={() => removeGuest.mutate(guest.userId)} />
              ))}
              {invites.map((pending) => (
                <InviteRow key={pending.id} invite={pending} canManage={canManage} onCancel={() => cancelInvite.mutate(pending.id)} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-7 flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            onClose();
            navigate("/dashboard/settings?page=clients");
          }}
          className="inline-flex items-center gap-2 text-[13.5px] text-co-text2 outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
        >
          <ArrowUpRight className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
          All clients in Settings
        </button>
        <span className="ml-auto" />
        <KitButton variant="ghost" onClick={onClose}>
          Done
        </KitButton>
      </div>
    </KitModal>
  );
}
